import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search, X, ArrowRight, Loader,
  Users, Baby, Briefcase, FileBadge,
  Package, Truck, Wrench, Hammer, CheckSquare,
} from 'lucide-react';
import { globalSearch } from '../api';
import { useImpersonate } from '../contexts/ImpersonateContext';

const RECENTS_KEY_PREFIX = 'global-search-recents-v2-';
const MAX_RECENTS = 5;
const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

// Mapping entity_type → module SuiviPlus (cohérent avec backend)
const ENTITY_TO_MODULE = {
  employee: 'hr',
  child: 'creche',
  application: 'recruitment',
  job_offer: 'recruitment',
  product: 'stocks',
  supplier: 'stocks',
  equipment: 'equipment',
  work_order: 'equipment',
  task: 'tasks',
};

// Ordre d'affichage des catégories (cohérent avec backend)
const ENTITY_DISPLAY_ORDER = [
  'employee', 'child', 'application', 'job_offer',
  'product', 'supplier', 'equipment', 'work_order', 'task',
];

function isEntityAccessible(entityType, effectiveRole, effectiveModules) {
  if (effectiveRole === 'admin') return true;
  const requiredModule = ENTITY_TO_MODULE[entityType];
  if (!requiredModule) return false;
  let mods = effectiveModules;
  if (typeof mods === 'string') {
    try { mods = JSON.parse(mods); } catch { mods = []; }
  }
  return Array.isArray(mods) && mods.includes(requiredModule);
}

// Métadonnées de chaque type d'entité
const ENTITY_META = {
  employee:   { label: 'Employés',     icon: Users,      color: 'bg-teal-100 text-teal-700',     accent: 'border-l-teal-500' },
  child:      { label: 'Enfants',      icon: Baby,       color: 'bg-purple-100 text-purple-700', accent: 'border-l-purple-500' },
  application:{ label: 'Candidatures', icon: Briefcase,  color: 'bg-blue-100 text-blue-700',     accent: 'border-l-blue-500' },
  job_offer:  { label: 'Offres',       icon: FileBadge,  color: 'bg-sky-100 text-sky-700',       accent: 'border-l-sky-500' },
  product:    { label: 'Produits',     icon: Package,    color: 'bg-amber-100 text-amber-700',   accent: 'border-l-amber-500' },
  supplier:   { label: 'Fournisseurs', icon: Truck,      color: 'bg-orange-100 text-orange-700', accent: 'border-l-orange-500' },
  equipment:  { label: 'Équipements',  icon: Wrench,     color: 'bg-rose-100 text-rose-700',     accent: 'border-l-rose-500' },
  work_order: { label: 'Travaux',      icon: Hammer,     color: 'bg-red-100 text-red-700',       accent: 'border-l-red-500' },
  task:       { label: 'Tâches',       icon: CheckSquare,color: 'bg-yellow-100 text-yellow-700', accent: 'border-l-yellow-500' },
};

// Surligne les caractères de la query dans le texte (case-insensitive, insensible aux accents)
function highlightMatch(text, query) {
  if (!text || !query) return text;
  const normalize = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const nText = normalize(text);
  const nQuery = normalize(query.trim());
  if (nQuery.length < 1) return text;

  // Trouve les positions de matches
  const parts = [];
  let lastEnd = 0;
  let idx = nText.indexOf(nQuery);
  while (idx !== -1) {
    if (idx > lastEnd) parts.push({ text: text.slice(lastEnd, idx), highlight: false });
    parts.push({ text: text.slice(idx, idx + nQuery.length), highlight: true });
    lastEnd = idx + nQuery.length;
    idx = nText.indexOf(nQuery, lastEnd);
  }
  if (lastEnd < text.length) parts.push({ text: text.slice(lastEnd), highlight: false });
  if (parts.length === 0) return text;

  return parts.map((p, i) =>
    p.highlight ? (
      <mark key={i} className="bg-yellow-200 text-gray-900 rounded px-0.5">{p.text}</mark>
    ) : (
      <span key={i}>{p.text}</span>
    )
  );
}

const GlobalSearchModal = ({ open, onClose, onNavigate }) => {
  const { effectiveUser, isImpersonating, impersonatedUser, realUser } = useImpersonate();

  // ID de l'utilisateur effectif pour scoper le localStorage des récents
  // - Si impersonate : id du user simulé (_impersonatedUserId)
  // - Sinon : id du user réel
  const effectiveUserId = isImpersonating
    ? (impersonatedUser?._impersonatedUserId || `impersonate-anon`)
    : (realUser?.id || realUser?.sub || 'anon');
  const recentsKey = `${RECENTS_KEY_PREFIX}${effectiveUserId}`;

  // Permissions effectives pour filtrer les récents (cohérent avec backend)
  const effectiveRole = effectiveUser?.role;
  const effectiveModules = effectiveUser?.modules;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ groups: {}, total: 0 });
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const flatListRef = useRef([]);

  // Reset à l'ouverture
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults({ groups: {}, total: 0 });
    setActiveIndex(-1);
    setTimeout(() => inputRef.current?.focus(), 50);
    try {
      const stored = JSON.parse(localStorage.getItem(recentsKey) || '[]');
      // Filtre défensif : retire les récents devenus inaccessibles (permissions modifiées)
      const accessible = stored.filter((r) =>
        isEntityAccessible(r.entityType, effectiveRole, effectiveModules)
      );
      setRecents(accessible);
    } catch {
      setRecents([]);
    }
  }, [open, recentsKey, effectiveRole, effectiveModules]);

  // Reset active index à chaque frappe
  useEffect(() => { setActiveIndex(-1); }, [query]);

  // Recherche debounced
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      setResults({ groups: {}, total: 0 });
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      globalSearch(q)
        .then((res) => setResults(res.data || { groups: {}, total: 0 }))
        .catch(() => setResults({ groups: {}, total: 0 }))
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, open]);

  // Construit la liste à plat pour navigation clavier (suit l'ordre d'affichage)
  const buildFlatList = useCallback(() => {
    const flat = [];
    const groups = results.groups || {};
    ENTITY_DISPLAY_ORDER.forEach((type) => {
      (groups[type] || []).forEach((item) => flat.push(item));
    });
    return flat;
  }, [results]);

  // Met à jour la flat list quand les résultats changent
  useEffect(() => {
    flatListRef.current = buildFlatList();
  }, [results, buildFlatList]);

  const handleSelect = useCallback((item) => {
    // Garde-fou : double check que l'user effectif peut accéder à ce type
    if (!isEntityAccessible(item.entityType, effectiveRole, effectiveModules)) {
      // Cas rare (permissions modifiées entre le moment où le récent a été stocké et le clic)
      // On retire silencieusement l'item et on ne navigue pas
      const newRecents = recents.filter((r) => !(r.entityType === item.entityType && r.entityId === item.entityId));
      setRecents(newRecents);
      try { localStorage.setItem(recentsKey, JSON.stringify(newRecents)); } catch {}
      return;
    }
    // Ajoute aux récents (max 5, dédoublonné)
    const newRecent = {
      entityType: item.entityType,
      entityId: item.entityId,
      title: item.title,
      subtitle: item.subtitle,
    };
    const newRecents = [
      newRecent,
      ...recents.filter((r) => !(r.entityType === item.entityType && r.entityId === item.entityId)),
    ].slice(0, MAX_RECENTS);
    setRecents(newRecents);
    try {
      localStorage.setItem(recentsKey, JSON.stringify(newRecents));
    } catch {}
    onNavigate?.(item);
    onClose();
  }, [recents, recentsKey, effectiveRole, effectiveModules, onNavigate, onClose]);

  // Navigation clavier
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      const list = flatListRef.current;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, list.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = list[activeIndex] ?? list[0];
        if (item) handleSelect(item);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, activeIndex, handleSelect]);

  // Scroll auto vers item actif
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = document.querySelector(`[data-search-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  const showRecents = query.trim().length < MIN_QUERY;
  const groups = results.groups || {};
  const orderedTypes = ENTITY_DISPLAY_ORDER.filter((t) => (groups[t] || []).length > 0);
  let flatIdx = -1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 pt-[10vh] px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Champ de recherche */}
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search size={18} className="text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un employé, un enfant, un candidat…"
            className="flex-1 outline-none text-sm text-gray-800 placeholder-gray-400"
          />
          {loading && <Loader size={14} className="animate-spin text-gray-400" />}
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] text-gray-400 border border-gray-200 rounded">Esc</kbd>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto">
          {showRecents && recents.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                Récemment consultés
              </div>
              {recents.map((r) => {
                flatIdx++;
                const meta = ENTITY_META[r.entityType] || ENTITY_META.employee;
                const Icon = meta.icon;
                return (
                  <button
                    key={`${r.entityType}-${r.entityId}`}
                    data-search-idx={flatIdx}
                    onClick={() => handleSelect(r)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 border-l-2 border-l-transparent`}
                  >
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${meta.color}`}>
                      <Icon size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-800 truncate">{r.title}</div>
                      {r.subtitle && <div className="text-xs text-gray-400 truncate">{r.subtitle}</div>}
                    </div>
                    <ArrowRight size={14} className="text-gray-300" />
                  </button>
                );
              })}
            </div>
          )}

          {showRecents && recents.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-gray-400">
              <Search size={28} className="mx-auto mb-3 text-gray-300" />
              <p>Tapez au moins 2 caractères pour rechercher</p>
              <p className="mt-1 text-xs">Employés · Enfants · Candidatures</p>
            </div>
          )}

          {!showRecents && results.total === 0 && !loading && query.trim().length >= MIN_QUERY && (
            <div className="px-6 py-12 text-center text-sm text-gray-400">
              Aucun résultat pour <span className="font-medium text-gray-600">« {query.trim()} »</span>
            </div>
          )}

          {!showRecents && orderedTypes.map((type) => {
            const meta = ENTITY_META[type];
            const Icon = meta.icon;
            const items = groups[type] || [];
            return (
              <div key={type}>
                <div className={`flex items-center gap-2 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide ${meta.color}`}>
                  <Icon size={12} />
                  {meta.label}
                  <span className="ml-auto text-[10px] opacity-70">{items.length}</span>
                </div>
                {items.map((item) => {
                  flatIdx++;
                  const active = activeIndex === flatIdx;
                  return (
                    <button
                      key={`${item.entityType}-${item.entityId}`}
                      data-search-idx={flatIdx}
                      onClick={() => handleSelect(item)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-l-2 ${
                        active ? `bg-gray-50 ${meta.accent}` : 'border-l-transparent hover:bg-gray-50'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${meta.color}`}>
                        <Icon size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-800 truncate">
                          {highlightMatch(item.title, query)}
                        </div>
                        {item.subtitle && (
                          <div className="text-xs text-gray-400 truncate">{item.subtitle}</div>
                        )}
                      </div>
                      {active && <kbd className="text-[9px] text-gray-400 border border-gray-200 rounded px-1 py-0.5 flex-shrink-0">↵</kbd>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-gray-50 flex items-center justify-between text-[10px] text-gray-400">
          <div className="flex items-center gap-3">
            <span><kbd className="px-1 py-0.5 border border-gray-200 rounded bg-white">↑↓</kbd> Naviguer</span>
            <span><kbd className="px-1 py-0.5 border border-gray-200 rounded bg-white">↵</kbd> Sélectionner</span>
            <span><kbd className="px-1 py-0.5 border border-gray-200 rounded bg-white">Esc</kbd> Fermer</span>
          </div>
          {results.total > 0 && (
            <span>{results.total} résultat{results.total > 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchModal;
